
import React, { useState, useEffect } from 'react';
import { Promo } from '../../types';
import { StorageService } from '../../services/storageService';
import { 
    Tag, Plus, Trash2, Edit, Save, X, 
    Sparkles, Search, Globe, Image as ImageIcon, 
    Zap, Loader2, CheckCircle, AlertTriangle, 
    ExternalLink, Command, ShieldCheck, Copy,
    Layout, Smartphone, Monitor
} from 'lucide-react';

export const PromoManager: React.FC = () => {
    const [promos, setPromos] = useState<Promo[]>([]);
    const [selectedPromo, setSelectedPromo] = useState<Partial<Promo> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const data = await StorageService.getAdminPromos();
            setPromos(data);
        } catch (e) {
            setStatus({ type: 'error', text: 'Failed to load promotion registry.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedPromo?.title || !selectedPromo?.message) return;
        setIsSaving(true);
        setStatus(null);
        try {
            await StorageService.savePromo(selectedPromo);
            setStatus({ type: 'success', text: 'Promotion successfully published.' });
            setSelectedPromo(null);
            loadData();
        } catch (e) {
            setStatus({ type: 'error', text: 'Commmit failure. Check server connectivity.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Permanently purge this promotion?')) return;
        try {
            await StorageService.deletePromo(id);
            loadData();
            setStatus({ type: 'success', text: 'Offer purged from registry.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Purge protocol failed.' });
        }
    };

    const toggleActive = async (promo: Promo) => {
        try {
            // First disable all others if we're enabling this one (single active promo rule)
            if (!promo.isActive) {
                for (const p of promos) {
                    if (p.isActive && p._id !== promo._id) {
                        await StorageService.savePromo({ ...p, isActive: false });
                    }
                }
            }
            await StorageService.savePromo({ ...promo, isActive: !promo.isActive });
            loadData();
        } catch (e) {
            setStatus({ type: 'error', text: 'Toggle protocol failure.' });
        }
    };

    return (
        <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900 max-w-7xl mx-auto">
            
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-950 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/10">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><Tag size={180}/></div>
                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl border-b-4 border-indigo-800">
                            <Sparkles size={24} />
                        </div>
                        <h2 className="text-3xl font-black tracking-tight">Promotion Architect</h2>
                    </div>
                    <p className="text-slate-400 font-medium text-sm md:text-lg max-w-2xl leading-relaxed">
                        Design sticky bottom banners for limited-time coupons and premium upgrades. Only one promotion can be active in the registry at a time.
                    </p>
                    
                    <div className="mt-8">
                        <button 
                            onClick={() => setSelectedPromo({ title: 'NEW OFFER', message: 'Take 20% off Elite Pro today!', isActive: false })}
                            className="bg-white text-slate-900 px-8 py-4 rounded-2xl font-black text-xs tracking-widest flex items-center gap-2 hover:bg-emerald-50 transition-all active:scale-95 border-b-4 border-slate-200"
                        >
                            <Plus size={18} /> Provision New Offer
                        </button>
                    </div>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertTriangle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                {/* LIST VIEW */}
                <div className="xl:col-span-4 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
                    <div className="p-6 bg-slate-50 border-b border-slate-100 font-black text-[10px] tracking-widest text-slate-400">Offer History</div>
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-3 bg-slate-50/30">
                        {isLoading ? (
                            <div className="p-10 text-center opacity-30 animate-pulse"><Loader2 className="animate-spin mx-auto" /></div>
                        ) : promos.length === 0 ? (
                            <div className="p-12 text-center opacity-20">
                                <Layout size={48} className="mx-auto mb-4" />
                                <p className="font-black text-[10px]">No Promos provisioned</p>
                            </div>
                        ) : promos.map(p => (
                            <div 
                                key={p._id} 
                                onClick={() => setSelectedPromo(p)}
                                className={`p-5 bg-white rounded-3xl border-2 transition-all cursor-pointer group relative overflow-hidden ${selectedPromo?._id === p._id ? 'border-indigo-500 shadow-xl translate-x-1' : 'border-transparent hover:border-slate-200'}`}
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <span className={`px-2 py-0.5 rounded-lg text-[7px] font-black ${p.isActive ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-slate-100 text-slate-400'}`}>
                                        {p.isActive ? 'ACTIVE IN HUB' : 'DRAFT'}
                                    </span>
                                    <button onClick={(e) => { e.stopPropagation(); toggleActive(p); }} className={`p-1.5 rounded-lg transition-all ${p.isActive ? 'text-indigo-600 bg-indigo-50' : 'text-slate-300 hover:text-slate-500'}`} title="Toggle Visibility">
                                        <Zap size={14} className={p.isActive ? 'fill-current' : ''} />
                                    </button>
                                </div>
                                <h4 className="font-black text-xs tracking-tight text-slate-800">{p.title}</h4>
                                <p className="text-[10px] text-slate-400 font-medium truncate mt-1">"{p.message}"</p>
                                <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p._id!); }} className="p-2 text-rose-300 hover:text-rose-600"><Trash2 size={14}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* EDITOR PANEL */}
                {selectedPromo ? (
                    <div className="xl:col-span-8 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden relative">
                        <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar space-y-12">
                            <div className="flex justify-between items-center border-b border-slate-50 pb-8">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl"><Command size={24}/></div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 tracking-tight">Banner Blueprint</h3>
                                        <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Configuring Operational Promotion</p>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedPromo(null)} className="p-3 bg-slate-50 text-slate-400 rounded-2xl hover:text-rose-500 transition-all border border-slate-100"><X size={24} /></button>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                <div className="space-y-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Protocol Header (Title)</label>
                                        <input 
                                            type="text" value={selectedPromo.title} 
                                            onChange={e => setSelectedPromo({...selectedPromo, title: e.target.value.toUpperCase()})}
                                            className="w-full p-5 bg-slate-50 border border-slate-100 rounded-2xl font-black text-lg outline-none focus:bg-white shadow-inner" 
                                            placeholder="LIMITED TIME OFFER..."
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Narrative Instruction (Message)</label>
                                        <textarea 
                                            value={selectedPromo.message} 
                                            onChange={e => setSelectedPromo({...selectedPromo, message: e.target.value})}
                                            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[140px] font-bold text-slate-700 outline-none focus:bg-white resize-none shadow-inner leading-relaxed" 
                                            placeholder="The main message to the user..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><ImageIcon size={14}/> Image Source (URL)</label>
                                            <input 
                                                type="text" value={selectedPromo.imageUrl} 
                                                onChange={e => setSelectedPromo({...selectedPromo, imageUrl: e.target.value})}
                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-mono text-[10px] outline-none" 
                                                placeholder="https://..."
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><ExternalLink size={14}/> Destination URL</label>
                                            <input 
                                                type="text" value={selectedPromo.linkUrl} 
                                                onChange={e => setSelectedPromo({...selectedPromo, linkUrl: e.target.value})}
                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-mono text-[10px] outline-none" 
                                                placeholder="https://..."
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Copy size={14}/> Coupon Logic (Code)</label>
                                            <input 
                                                type="text" value={selectedPromo.couponCode} 
                                                onChange={e => setSelectedPromo({...selectedPromo, couponCode: e.target.value.toUpperCase()})}
                                                className="w-full p-4 bg-slate-900 border border-slate-800 rounded-2xl font-mono text-emerald-400 tracking-widest shadow-xl outline-none" 
                                                placeholder="ELITE20"
                                            />
                                        </div>
                                    </div>

                                    {/* PREVIEW RENDERING */}
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest">Live Execution Preview</label>
                                            <div className="flex bg-slate-100 p-1 rounded-xl">
                                                <button onClick={() => setPreviewMode('desktop')} className={`p-2 rounded-lg transition-all ${previewMode === 'desktop' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}><Monitor size={14}/></button>
                                                <button onClick={() => setPreviewMode('mobile')} className={`p-2 rounded-lg transition-all ${previewMode === 'mobile' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}><Smartphone size={14}/></button>
                                            </div>
                                        </div>
                                        <div className={`p-6 bg-slate-100 rounded-[2rem] border-4 border-slate-200 border-dashed transition-all duration-500 overflow-hidden flex items-center justify-center ${previewMode === 'mobile' ? 'max-w-[320px] mx-auto' : 'w-full'}`}>
                                            <div className="w-full bg-slate-900 text-white rounded-3xl p-4 shadow-2xl border border-white/5 flex flex-col md:flex-row items-center gap-4 relative overflow-hidden">
                                                <div className="absolute top-0 right-0 w-24 h-full bg-indigo-500/5 pointer-events-none"></div>
                                                <div className="flex items-center gap-3 w-full">
                                                    <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0">
                                                        <Sparkles size={24} />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-[8px] font-black text-amber-400 tracking-widest">{selectedPromo.title || 'HEADER'}</p>
                                                        <p className="text-[11px] font-bold leading-tight">{selectedPromo.message || 'Promotion message will appear here.'}</p>
                                                    </div>
                                                </div>
                                                <div className="w-full md:w-auto mt-2 md:mt-0">
                                                    <div className="px-6 py-2 bg-indigo-500 rounded-xl text-[9px] font-black text-center">Claim</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 border-t border-slate-50 bg-white flex justify-end gap-4 shrink-0">
                            <div className="flex items-center gap-4 mr-auto px-6 py-2 bg-slate-50 rounded-2xl border border-slate-100">
                                <button onClick={() => setSelectedPromo({...selectedPromo, isActive: !selectedPromo.isActive})} className={`transition-all ${selectedPromo.isActive ? 'text-indigo-600' : 'text-slate-300'}`}>
                                    <div className={`relative w-12 h-6 rounded-full transition-all duration-300 ${selectedPromo.isActive ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                                        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform ${selectedPromo.isActive ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </div>
                                </button>
                                <div>
                                    <p className="text-[10px] font-black text-slate-800 tracking-tight">Active Protocol</p>
                                    <p className={`text-[8px] font-black ${selectedPromo.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>{selectedPromo.isActive ? 'OPERATIONAL' : 'DRAFT PHASE'}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedPromo(null)} className="px-8 py-4 font-black text-[10px] text-slate-400">Discard</button>
                            <button 
                                onClick={handleSave} 
                                disabled={isSaving}
                                className="px-14 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-2xl hover:bg-black transition-all active:scale-[0.95] disabled:opacity-50 border-b-4 border-black flex items-center gap-3"
                            >
                                {isSaving ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} Commit Registry
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="xl:col-span-8 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-60">
                        <div className="w-24 h-24 bg-white rounded-[2.5rem] flex items-center justify-center text-slate-200 shadow-xl mb-8 group-hover:scale-[1.1] transition-transform">
                            <Tag size={48}/>
                        </div>
                        <h4 className="text-xl font-black text-slate-900 tracking-tighter">Promo Workspace Empty</h4>
                        <p className="font-black text-slate-400 tracking-[0.3em] text-[10px] mt-2">Provision a new limited-time offer or manage existing coupons</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);
